import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Puzzle,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Activity,
  Zap,
  TrendingUp,
  Edit,
  Trash2,
  Play,
  Pause,
  Send,
  Loader2,
  Download,
  Star,
  Filter,
  Target,
  Shield,
  Radio,
  Crosshair,
  Bot,
  Upload,
  FileText
} from "lucide-react";
import axios from "axios";
import ParticleBackground, { MatrixBackground } from "./ParticleBackground";
import MatrixRain from "./MatrixRain";
import MissionControl from "./MissionControl";
import WarRoom from "./WarRoom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Alpha1 Logo Component
const Alpha1Logo = ({ size = "default" }) => {
  const sizeClasses = { small: "w-8 h-8", default: "w-10 h-10" };
  return (
    <div className={`${sizeClasses[size]} relative`}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00FF41" />
            <stop offset="100%" stopColor="#00FFC8" />
          </linearGradient>
        </defs>
        <polygon points="50,2 95,25 95,75 50,98 5,75 5,25" fill="none" stroke="url(#logoGrad)" strokeWidth="2"/>
        <circle cx="50" cy="50" r="5" fill="#00FF41"/>
        <text x="50" y="58" textAnchor="middle" fill="#00FF41" fontSize="20" fontFamily="Orbitron" fontWeight="bold">α1</text>
      </svg>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, user, onLogout, isMobileOpen, setIsMobileOpen }) => {
  const navItems = [
    { id: "overview", label: "Command Center", icon: LayoutDashboard },
    { id: "mission-control", label: "Mission Control", icon: Crosshair },
    { id: "war-room", label: "War Room", icon: Target },
    { id: "agents", label: "Agent Ops", icon: Users },
    { id: "skills", label: "Skills Arsenal", icon: Puzzle },
    { id: "ai-chat", label: "AI Systems", icon: MessageSquare },
    { id: "settings", label: "Config", icon: Settings },
  ];

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-40 lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#000a00] border-r border-primary/20 z-50
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between border-b border-primary/20">
            <div className="flex items-center gap-3">
              <Alpha1Logo />
              <div className="flex flex-col">
                <span className="font-military text-lg tracking-wider text-primary">ALPHA1-ZERO</span>
                <span className="text-[8px] font-mono text-primary/50 tracking-widest">SKIDRIVE TECH</span>
              </div>
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden text-primary hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav items */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsMobileOpen(false);
                }}
                data-testid={`nav-${item.id}`}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 transition-all font-mono text-sm tracking-wider
                  ${activeTab === item.id
                    ? 'bg-primary/10 text-primary border-l-2 border-primary'
                    : 'text-primary/50 hover:text-primary hover:bg-primary/5'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-primary/20">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded border border-primary/30" />
              ) : (
                <div className="w-10 h-10 border border-primary/30 flex items-center justify-center">
                  <span className="text-primary font-bold font-mono">{user?.name?.[0] || 'U'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono text-primary truncate">{user?.name || 'Operator'}</p>
                <p className="text-[10px] text-primary/40 truncate font-mono">{user?.email || ''}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-4 py-2 text-primary/50 hover:text-red-400 hover:bg-red-400/10 transition-colors font-mono text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>DISCONNECT</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="cyber-card p-6 border border-primary/20"
  >
    <div className="flex items-start justify-between">
      <div className="w-12 h-12 border border-primary/30 flex items-center justify-center bg-primary/5">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      {trend && (
        <span className={`text-xs flex items-center gap-1 font-mono ${trend > 0 ? 'text-primary' : 'text-red-400'}`}>
          <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div className="mt-4">
      <p className="text-3xl font-orbitron font-bold text-primary">{value}</p>
      <p className="text-[10px] text-primary/50 mt-1 font-mono tracking-wider uppercase">{label}</p>
    </div>
  </motion.div>
);

// Overview Tab
const OverviewTab = ({ stats }) => (
  <div className="space-y-6">
    <div>
      <h2 className="font-military text-2xl mb-2 text-primary">COMMAND CENTER</h2>
      <p className="text-primary/50 font-mono text-sm">Real-time operational status monitoring</p>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={Users} label="Total Agents" value={stats.total_agents} trend={12} color="primary" />
      <StatCard icon={Activity} label="Active Ops" value={stats.active_agents} color="secondary" />
      <StatCard icon={Puzzle} label="Skills Loaded" value={stats.available_skills} trend={8} color="green" />
      <StatCard icon={Zap} label="Tasks Complete" value={stats.tasks_completed} trend={24} color="primary" />
    </div>

    {/* Activity feed */}
    <div className="cyber-card p-6 border border-primary/20">
      <h3 className="font-orbitron text-lg font-bold mb-4 flex items-center gap-2 text-primary">
        <Activity className="w-5 h-5" />
        ACTIVITY LOG
      </h3>
      <div className="space-y-3">
        {[
          { action: "Agent deployed", agent: "Code Review Bot", time: "2 min ago", status: "SUCCESS" },
          { action: "Task completed", agent: "Research Assistant", time: "15 min ago", status: "SUCCESS" },
          { action: "Skill installed", agent: "DevOps Agent", time: "1 hour ago", status: "SUCCESS" },
          { action: "Status changed", agent: "Legal Analyzer", time: "3 hours ago", status: "INFO" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-4 p-3 border border-primary/10 bg-black/30">
            <div className={`w-2 h-2 ${item.status === 'SUCCESS' ? 'bg-primary' : 'bg-secondary'}`} />
            <div className="flex-1 font-mono text-sm">
              <span className="text-primary/50">{item.action}:</span>{' '}
              <span className="text-primary">{item.agent}</span>
            </div>
            <span className="text-[10px] text-primary/30 font-mono">{item.time}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Agents Tab
const AgentsTab = ({ agents, onCreateAgent, onUpdateAgent, onDeleteAgent, onUpdateStatus }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    agent_type: "developer",
    skills: []
  });

  const agentTypes = [
    { value: "developer", label: "Developer", icon: "code" },
    { value: "researcher", label: "Researcher", icon: "book-open" },
    { value: "legal", label: "Legal Analyst", icon: "scale" },
    { value: "devops", label: "DevOps Engineer", icon: "server" },
    { value: "healthcare", label: "Healthcare Analyst", icon: "heart" },
    { value: "government", label: "Government Ops", icon: "building" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingAgent) {
      await onUpdateAgent(editingAgent.agent_id, formData);
    } else {
      await onCreateAgent(formData);
    }
    setShowModal(false);
    setEditingAgent(null);
    setFormData({ name: "", description: "", agent_type: "developer", skills: [] });
  };

  const openEditModal = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      description: agent.description,
      agent_type: agent.agent_type,
      skills: agent.skills || []
    });
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-rajdhani text-2xl font-bold mb-2">AI Agents</h2>
          <p className="text-gray-400">Manage your autonomous workforce</p>
        </div>
        <button
          onClick={() => {
            setEditingAgent(null);
            setFormData({ name: "", description: "", agent_type: "developer", skills: [] });
            setShowModal(true);
          }}
          data-testid="create-agent-btn"
          className="cyber-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Deploy Agent
        </button>
      </div>

      {/* Agents grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((agent) => (
          <motion.div
            key={agent.agent_id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cyber-card p-6 group"
            data-testid={`agent-card-${agent.agent_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/20 rounded flex items-center justify-center">
                  <Bot className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-rajdhani font-bold">{agent.name}</h3>
                  <span className="text-xs text-gray-500 uppercase">{agent.agent_type}</span>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${
                agent.status === 'active' ? 'status-active' :
                agent.status === 'paused' ? 'status-paused' : 'status-offline'
              }`} />
            </div>

            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{agent.description}</p>

            {agent.skills?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {agent.skills.slice(0, 3).map((skill, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-white/5 rounded text-gray-400">
                    {skill}
                  </span>
                ))}
                {agent.skills.length > 3 && (
                  <span className="text-xs px-2 py-1 text-gray-500">+{agent.skills.length - 3}</span>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 pt-4 border-t border-white/5">
              <button
                onClick={() => onUpdateStatus(agent.agent_id, agent.status === 'active' ? 'paused' : 'active')}
                data-testid={`agent-toggle-${agent.agent_id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded text-sm transition-colors ${
                  agent.status === 'active'
                    ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                    : 'bg-accent-green/10 text-accent-green hover:bg-accent-green/20'
                }`}
              >
                {agent.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {agent.status === 'active' ? 'Pause' : 'Start'}
              </button>
              <button
                onClick={() => openEditModal(agent)}
                data-testid={`agent-edit-${agent.agent_id}`}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded transition-colors"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDeleteAgent(agent.agent_id)}
                data-testid={`agent-delete-${agent.agent_id}`}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}

        {agents.length === 0 && (
          <div className="col-span-full cyber-card p-12 text-center">
            <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="font-rajdhani text-xl font-bold mb-2">No Agents Deployed</h3>
            <p className="text-gray-500 mb-6">Deploy your first AI agent to get started</p>
            <button
              onClick={() => setShowModal(true)}
              className="cyber-btn"
              data-testid="create-first-agent-btn"
            >
              Deploy First Agent
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="cyber-card p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-rajdhani text-xl font-bold mb-6">
                {editingAgent ? 'Edit Agent' : 'Deploy New Agent'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="agent-form">
                <div>
                  <label className="block text-xs font-mono text-gray-400 uppercase tracking-wider mb-2">
                    Agent Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="cyber-input w-full"
                    placeholder="Code Review Bot"
                    data-testid="agent-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-gray-400 uppercase tracking-wider mb-2">
                    Agent Type *
                  </label>
                  <select
                    value={formData.agent_type}
                    onChange={(e) => setFormData({ ...formData, agent_type: e.target.value })}
                    className="cyber-input w-full"
                    data-testid="agent-type-select"
                  >
                    {agentTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-gray-400 uppercase tracking-wider mb-2">
                    Description *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="cyber-input w-full resize-none"
                    placeholder="Describe what this agent does..."
                    data-testid="agent-description-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-3 border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 cyber-btn"
                    data-testid="agent-submit-btn"
                  >
                    {editingAgent ? 'Update Agent' : 'Deploy Agent'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Skills Marketplace Tab
const readFileBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const SkillsTab = ({ skills, user, onChanged, onUse }) => {
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", instructions: "" });
  const fileRef = useRef(null);

  const categories = ["all", ...Array.from(new Set(skills.map((s) => s.category).filter(Boolean)))];
  const filteredSkills = filter === "all"
    ? skills
    : skills.filter(s => s.category === filter);

  const errorText = (error) => error.response?.data?.detail || error.message;

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    setNotice(null);
    const added = [], failed = [];
    for (const file of Array.from(files)) {
      try {
        const content_base64 = await readFileBase64(file);
        const res = await axios.post(`${API}/skills/upload`, { filename: file.name, content_base64 }, { withCredentials: true });
        added.push(...res.data.saved.map((s) => s.name));
      } catch (error) {
        failed.push(`${file.name}: ${errorText(error)}`);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
    setBusy(false);
    setNotice({
      ok: failed.length === 0,
      text: [added.length && `Added: ${added.join(", ")}`, failed.length && `Failed: ${failed.join("; ")}`].filter(Boolean).join(" — ")
    });
    onChanged();
  };

  const handleCreate = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/skills`, form, { withCredentials: true });
      setNotice({ ok: true, text: `Added: ${res.data.name}` });
      setForm({ name: "", description: "", instructions: "" });
      setShowForm(false);
      onChanged();
    } catch (error) {
      setNotice({ ok: false, text: errorText(error) });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (skill) => {
    try {
      await axios.delete(`${API}/skills/${skill.skill_id}`, { withCredentials: true });
      setNotice({ ok: true, text: `Deleted: ${skill.name}` });
      onChanged();
    } catch (error) {
      setNotice({ ok: false, text: errorText(error) });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-rajdhani text-2xl font-bold mb-2">Skills Arsenal</h2>
          <p className="text-gray-400">Upload SKILL.md files or .zip skill packs, then use them in Mission Control</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".md,.txt,.zip"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="skill-upload-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="cyber-btn flex items-center gap-2"
            data-testid="skill-upload-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload Skill
          </button>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="px-4 py-2 border border-secondary/50 text-secondary hover:bg-secondary/10 transition-colors rounded text-sm font-medium flex items-center gap-2"
            data-testid="skill-new-btn"
          >
            <Plus className="w-4 h-4" />
            New Skill
          </button>
          <Filter className="w-4 h-4 text-gray-500 ml-2" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="cyber-input text-sm py-2"
            data-testid="skills-filter"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat === "all" ? "All Categories" : cat}</option>
            ))}
          </select>
        </div>
      </div>

      {notice && (
        <div className={`p-3 rounded border text-sm ${notice.ok ? "border-primary/30 bg-primary/5 text-primary" : "border-red-500/30 bg-red-500/5 text-red-400"}`} data-testid="skill-notice">
          {notice.text}
        </div>
      )}

      {showForm && (
        <div className="cyber-card p-6 space-y-4" data-testid="skill-form">
          <h3 className="font-rajdhani text-lg font-bold">New Skill</h3>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Skill name"
            className="cyber-input w-full"
          />
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="One-line description"
            className="cyber-input w-full"
          />
          <textarea
            value={form.instructions}
            onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            placeholder="Instructions: what the AI should do when this skill is in use"
            className="cyber-input w-full h-48 resize-y font-mono text-sm"
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={busy || !form.name.trim() || !form.instructions.trim()} className="cyber-btn">
              Save Skill
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-400 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
        {filteredSkills.map((skill) => (
          <motion.div
            key={skill.skill_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="holo-card p-6 flex flex-col"
            data-testid={`skill-card-${skill.skill_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-secondary/20 rounded flex items-center justify-center">
                {skill.has_instructions ? <FileText className="w-6 h-6 text-primary" /> : <Puzzle className="w-6 h-6 text-secondary" />}
              </div>
              {skill.created_by === user?.user_id ? (
                <button
                  onClick={() => handleDelete(skill)}
                  className="text-gray-500 hover:text-red-400"
                  title="Delete this skill"
                  data-testid={`delete-skill-${skill.skill_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm">{skill.rating}</span>
                </div>
              )}
            </div>

            <h3 className="font-rajdhani font-bold text-lg mb-1">{skill.name}</h3>
            <span className="text-xs text-secondary uppercase">{skill.category}</span>

            <p className="text-sm text-gray-400 mt-3 mb-4 line-clamp-3 flex-1">{skill.description}</p>

            {skill.has_instructions ? (
              <button
                onClick={() => onUse(skill.skill_id)}
                className="w-full mt-auto py-2 border border-primary/50 text-primary hover:bg-primary/10 transition-colors rounded text-sm font-medium flex items-center justify-center gap-2"
                data-testid={`use-skill-${skill.skill_id}`}
              >
                <Crosshair className="w-4 h-4" />
                Use in Mission Control
              </button>
            ) : (
              <div className="w-full mt-auto py-2 text-center text-xs text-gray-500 border border-white/5 rounded">
                Showcase only (no instructions)
              </div>
            )}
          </motion.div>
        ))}

        {filteredSkills.length === 0 && (
          <div className="col-span-full cyber-card p-12 text-center">
            <Puzzle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="font-rajdhani text-xl font-bold mb-2">No Skills Found</h3>
            <p className="text-gray-500">Upload a SKILL.md or .zip to add one</p>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Chat Tab
const AIChatTab = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [provider, setProvider] = useState("router");
  const [loading, setLoading] = useState(false);

  const providers = [
    { value: "router", label: "Auto Router", color: "text-primary" },
    { value: "openai", label: "GPT-5.2", color: "text-green-400" },
    { value: "anthropic", label: "Claude 4.5", color: "text-orange-400" },
    { value: "gemini", label: "Gemini 3", color: "text-blue-400" },
    { value: "openclaw", label: "OpenClaw", color: "text-cyan-400" },
  ];

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input, provider };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: input, provider },
        { withCredentials: true }
      );

      const aiMessage = {
        role: "assistant",
        content: response.data.response,
        provider: response.data.provider,
        model: response.data.model
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        role: "assistant",
        content: error.response?.data?.detail || "Sorry, I encountered an error. Please try again.",
        provider,
        error: true
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 h-[calc(100vh-12rem)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-rajdhani text-2xl font-bold mb-2">AI Model Comparison</h2>
          <p className="text-gray-400">Compare responses from different AI providers</p>
        </div>
        <div className="flex items-center gap-2">
          {providers.map((p) => (
            <button
              key={p.value}
              onClick={() => setProvider(p.value)}
              data-testid={`provider-btn-${p.value}`}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                provider === p.value
                  ? `bg-white/10 ${p.color} border border-white/20`
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="cyber-card flex flex-col h-[calc(100%-8rem)]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="font-rajdhani text-xl font-bold mb-2">Start a Conversation</h3>
              <p className="text-gray-500">Ask anything and compare AI responses</p>
            </div>
          )}
          
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] p-4 rounded-lg ${
                msg.role === 'user'
                  ? 'bg-primary/20 text-white'
                  : msg.error
                  ? 'bg-red-500/10 text-red-400'
                  : 'bg-white/5 text-gray-300'
              }`}>
                {msg.role === 'assistant' && msg.model && (
                  <div className="text-xs text-gray-500 mb-2 font-mono">
                    {msg.model}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/5 p-4 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-white/5">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask me anything..."
              className="cyber-input flex-1"
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="cyber-btn px-6"
              data-testid="chat-send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ user }) => (
  <div className="space-y-6">
    <div>
      <h2 className="font-rajdhani text-2xl font-bold mb-2">Settings</h2>
      <p className="text-gray-400">Manage your account and preferences</p>
    </div>

    <div className="cyber-card p-6">
      <h3 className="font-rajdhani text-lg font-bold mb-4">Profile Information</h3>
      <div className="flex items-center gap-4 mb-6">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full" />
        ) : (
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center">
            <span className="text-2xl text-primary font-bold">{user?.name?.[0] || 'U'}</span>
          </div>
        )}
        <div>
          <h4 className="text-xl font-bold">{user?.name}</h4>
          <p className="text-gray-400">{user?.email}</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase tracking-wider mb-2">
            Display Name
          </label>
          <input
            type="text"
            defaultValue={user?.name}
            className="cyber-input w-full max-w-md"
            data-testid="settings-name-input"
          />
        </div>
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase tracking-wider mb-2">
            Email Address
          </label>
          <input
            type="email"
            defaultValue={user?.email}
            disabled
            className="cyber-input w-full max-w-md opacity-50"
          />
        </div>
      </div>
    </div>

    <div className="cyber-card p-6">
      <h3 className="font-rajdhani text-lg font-bold mb-4">Danger Zone</h3>
      <p className="text-gray-400 text-sm mb-4">
        Once you delete your account, there is no going back. Please be certain.
      </p>
      <button className="px-6 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors rounded">
        Delete Account
      </button>
    </div>
  </div>
);

// Main Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [activeTab, setActiveTab] = useState("overview");
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({ total_agents: 0, active_agents: 0, available_skills: 0, tasks_completed: 0 });
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [missionSkill, setMissionSkill] = useState("");

  // Check auth on mount
  useEffect(() => {
    if (!location.state?.user) {
      checkAuth();
    }
  }, [location.state]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, agentsRes, skillsRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/agents`, { withCredentials: true }),
        axios.get(`${API}/skills`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setAgents(agentsRes.data);
      setSkills(skillsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
      axios.post(`${API}/seed`).catch(() => {});
    }
  }, [user, fetchData]);

  // Agent CRUD
  const handleCreateAgent = async (data) => {
    try {
      await axios.post(`${API}/agents`, data, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to create agent:", error);
    }
  };

  const handleUpdateAgent = async (agentId, data) => {
    try {
      await axios.put(`${API}/agents/${agentId}`, data, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to update agent:", error);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await axios.delete(`${API}/agents/${agentId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to delete agent:", error);
    }
  };

  const handleUpdateStatus = async (agentId, status) => {
    try {
      await axios.patch(`${API}/agents/${agentId}/status`, { status }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000a00] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-primary/50 font-mono text-sm tracking-wider">INITIALIZING SYSTEM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000a00] relative">
      <MatrixBackground />
      <MatrixRain />
      <div className="absolute inset-0 opacity-15">
        <ParticleBackground />
      </div>

      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        onLogout={handleLogout}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
      />

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-primary/20 p-4 flex items-center justify-between">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="lg:hidden text-primary hover:text-white"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50" />
              <input
                type="text"
                placeholder="Search agents, skills..."
                className="cyber-input w-full pl-10 py-2 text-sm"
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[10px] text-primary/30 hidden sm:block font-mono tracking-wider">
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
            </span>
          </div>
        </header>

        {/* Content */}
        <main className="p-6 relative">
          <AnimatePresence mode="wait">
            {activeTab === "overview" && (
              <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <OverviewTab stats={stats} />
              </motion.div>
            )}
            {activeTab === "mission-control" && (
              <motion.div key="mission-control" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <MissionControl agents={agents} stats={stats} skills={skills} initialSkillId={missionSkill} />
              </motion.div>
            )}
            {activeTab === "agents" && (
              <motion.div key="agents" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <AgentsTab
                  agents={agents}
                  onCreateAgent={handleCreateAgent}
                  onUpdateAgent={handleUpdateAgent}
                  onDeleteAgent={handleDeleteAgent}
                  onUpdateStatus={handleUpdateStatus}
                />
              </motion.div>
            )}
            {activeTab === "skills" && (
              <motion.div key="skills" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <SkillsTab skills={skills} user={user} onChanged={fetchData} onUse={(id) => { setMissionSkill(id); setActiveTab("mission-control"); }} />
              </motion.div>
            )}
            {activeTab === "ai-chat" && (
              <motion.div key="ai-chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <AIChatTab />
              </motion.div>
            )}
            {activeTab === "settings" && (
              <motion.div key="settings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <SettingsTab user={user} />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
